/**
 * The CRM shell's module registry (G0; docs/audits/ROLLOUT-CUSTOMERS-SHELL.md, closing note).
 *
 * The pilot built the shell around Quotes and named it in nine places. Each of those now reads
 * this list, so the next module (Customers) is an entry here plus its own files — not a hunt for
 * the string 'quotes'. One entry per module that routes inside itself:
 *
 *   id             the module's id; also its preferences document (user_ui_preferences.module)
 *   routeRoot      the path it owns; the landing route collapses anything under it to this
 *   boundaryPrefix MainLayout keys its error boundary by this, so /x -> /x/12 does not remount
 *   flyoutGroups   [{ section, navItem, ViewsMenu, viewsLabel, hideLabel }]: the Sidebar section
 *                  whose rail tap opens a flyout, the nav item that nests views, the views
 *                  component, and the expanded row's toggle labels: viewsLabel while closed,
 *                  hideLabel while open (polish 4)
 *   toolIds        the ids its toolRail may hold (preferenceShapes; the server keeps the same)
 *   features       a loader for its page-level tool registry and palette providers. Lazy on
 *                  purpose: see components/quotes/quoteModuleFeatures.js
 *   searchProvider its group in the search slot (the slot asks providers in registry order; a
 *                  provider with afterGroup is shown after that slot group, O6)
 *   guard          the unsaved-changes dialog's wording for its editor: { body, leave? }. With no
 *                  `leave` the dialog has no middle answer (a module that keeps no draft)
 *
 * Pure data and light components only: everything here is imported by the eagerly loaded shell.
 */
import QuoteViewsMenu from '../components/quotes/QuoteViewsMenu';
import CustomerViewsMenu from '../components/customers/CustomerViewsMenu';
import OrderViewsMenu from '../components/orders/OrderViewsMenu';
import { quoteSearchProvider } from '../components/quotes/quoteSearchProvider';
import { orderSearchProvider } from '../components/orders/orderSearchProvider';
import { TOOL_IDS_BY_MODULE } from '../contexts/preferenceShapes';

export const MODULES = Object.freeze([
  Object.freeze({
    id: 'quotes',
    routeRoot: '/quotes',
    boundaryPrefix: '/quotes',
    flyoutGroups: Object.freeze([
      Object.freeze({ section: 'sales', navItem: 'quotes', ViewsMenu: QuoteViewsMenu, viewsLabel: 'Show quotation views', hideLabel: 'Hide quotation views' })
    ]),
    toolIds: TOOL_IDS_BY_MODULE.quotes,
    features: () => import('../components/quotes/quoteModuleFeatures'),
    searchProvider: quoteSearchProvider,
    guard: Object.freeze({
      body: 'This quote has changes that are not saved yet. Your draft is kept on this device — you can pick it up again from Drafts.',
      leave: 'Leave — keep my draft on this device'
    })
  }),
  // C0 (ROLLOUT-CUSTOMERS-SHELL.md §6): records by URL and the editor guard. C3 adds its views to
  // the Sales flyout (D8: a group in Sales, not a section of its own). C4b + C5: its preferences
  // document (C4a's server), its tool ids and the lazy features loader (the tools, and since C6
  // the palette providers). No searchProvider, by decision (§3 D10): the slot's customer rows keep
  // coming from /api/search; C6 adds only the slot's "See all in Customers" footer row (SearchSlot).
  // No device drafts (§3 D4), so the guard has no `leave`: the dialog offers Keep editing and
  // Discard changes only.
  Object.freeze({
    id: 'customers',
    routeRoot: '/customers',
    boundaryPrefix: '/customers',
    flyoutGroups: Object.freeze([
      Object.freeze({ section: 'sales', navItem: 'customers', ViewsMenu: CustomerViewsMenu, viewsLabel: 'Show customer views', hideLabel: 'Hide customer views' })
    ]),
    toolIds: TOOL_IDS_BY_MODULE.customers,
    features: () => import('../components/customers/customerModuleFeatures'),
    guard: Object.freeze({
      body: 'This customer has changes that are not saved yet. If you leave now, they will be lost.'
    })
  }),
  // O0 (ROLLOUT-ORDERS-SHELL.md §6): records by URL and the amendment editor's guard. O3 adds its
  // views to the Sales flyout (D8: a group in Sales, after Customers and Quotations in nav order).
  // O4 + O5: its preferences document (no migration), its tool ids and the lazy features loader
  // (the tools, and since O6 the palette providers). O6: its search provider (§3 D9: /api/search
  // returns no orders), shown after Customers with "See all in Orders" after Customers' row. No
  // device drafts (§3 D4: the server keeps draft amendments), so the guard has no `leave`.
  Object.freeze({
    id: 'orders',
    routeRoot: '/orders',
    boundaryPrefix: '/orders',
    flyoutGroups: Object.freeze([
      Object.freeze({ section: 'sales', navItem: 'orders', ViewsMenu: OrderViewsMenu, viewsLabel: 'Show order views', hideLabel: 'Hide order views' })
    ]),
    toolIds: TOOL_IDS_BY_MODULE.orders,
    features: () => import('../components/orders/orderModuleFeatures'),
    searchProvider: orderSearchProvider,
    guard: Object.freeze({
      body: 'This amendment has changes that are not submitted yet. If you leave now, they will be lost.'
    })
  })
]);

/** The module with this id, or null. */
export const moduleById = (id, modules = MODULES) => modules.find((m) => m.id === id) || null;

/** The module whose route root owns this path (the root itself or anything under it), or null. */
export const moduleForPath = (pathname, modules = MODULES) => modules.find(
  (m) => pathname === m.routeRoot || (typeof pathname === 'string' && pathname.startsWith(`${m.routeRoot}/`))
) || null;

/** MainLayout's boundary prefixes, in registry order. */
export const boundaryPrefixes = (modules = MODULES) => modules.map((m) => m.boundaryPrefix);

/** Every flyout group, in registry order. */
export const flyoutGroups = (modules = MODULES) => modules.flatMap((m) => m.flyoutGroups || []);

/** The Sidebar sections whose rail tap opens a flyout, once each, in registry order. */
export const flyoutSections = (modules = MODULES) => [...new Set(flyoutGroups(modules).map((g) => g.section))];

/** The flyout group whose nav item nests views, or null. */
export const flyoutGroupForNavItem = (navItem, modules = MODULES) =>
  flyoutGroups(modules).find((g) => g.navItem === navItem) || null;

/** The search slot's module providers, in registry order. */
export const searchProviders = (modules = MODULES) => modules.map((m) => m.searchProvider).filter(Boolean);
